import { isSpecialPointReward } from "@/components/Ui/Label/Rewards/utils";
import type { SupSingleVaultV3 } from "@/types";
import React, { useMemo } from "react";

import { CRED_REWARD, CRED_SUPERVAULT_REWARD } from "@/components/Ui/Label/Rewards/constant";
import UiLabelRewardsTooltipLine from "@/components/Ui/Label/Rewards/TooltipLine";
import UiLabelApy, { getEmptyLabel, type SupDicApyPeriod } from "@/components/Ui/Label/Apy";

import { SupInsights } from "@repo/core-ui";
import useRewardSettings from "@/hooks/data/useRewardSettings";
import { SupText } from "@repo/core-ui";
import { getNetVaultApy } from "@/utils";
import dictionary from "@/../config/dictionary.config";
import config from "@/../config/dapp.config";

interface UiLabelRewardsTooltipProps {
  vault: SupSingleVaultV3;
  period?: SupDicApyPeriod;
  useFallback?: boolean;
  showCred?: boolean;
}

export default function UiLabelRewardsTooltip({ vault, period = "month", useFallback = false, showCred }: UiLabelRewardsTooltipProps) {
  const rewardsSettings = useRewardSettings();
  const { apyRewards, rewards } = useMemo(() => {
    return {
      apyRewards: ((rewardsSettings.rewardsOverloadFunc?.(vault.rewards ?? []) || vault.rewards) ?? []).filter(reward => reward.rate_type === "apy"),
      rewards: vault.rewards?.filter(reward => reward.rate_type !== "apy") ?? [],
    };
  }, [rewardsSettings, vault.rewards]);

  const netApy = useMemo(() => {
    const rawNetApy = getNetVaultApy(vault, rewardsSettings, period, useFallback);
    return rawNetApy === dictionary.statsDefaultValue ? getEmptyLabel(vault, period) : `${rawNetApy.toFixed(config.decimalsApyRoundingLimit)}%`;
  }, [period, rewardsSettings, useFallback, vault]);

  return (
    <div className="space-y-4 text-neutral-900">
      {(rewards.length > 0 || showCred) && (
        <div className="space-y-2">
          <SupText variant="subtitle2" weight="bold" className="h-4 w-[15rem]">
            Rewards
          </SupText>
          <div className="flex flex-col justify-center divide-y-[.5px] divide-neutral-75 self-stretch">
            {rewards.map((reward, index) => (
              <UiLabelRewardsTooltipLine reward={reward} key={`reward-tooltip-${vault.id}-${index}`} />
            ))}
            {showCred && <UiLabelRewardsTooltipLine reward={isSpecialPointReward(vault) ? CRED_SUPERVAULT_REWARD : CRED_REWARD} key={`reward-tooltip-${vault.id}-apy-cred`} />}
          </div>
        </div>
      )}

      {apyRewards.length > 0 && (
        <div className="flex flex-col justify-center space-y-2 self-stretch">
          <div className="inline-flex h-4 items-center space-x-1 self-stretch p-1">
            <SupInsights className="h-4 w-4" />
            <SupText variant="subtitle2" weight="bold" className="h-4 w-[15rem]">
              APY Boost
            </SupText>
          </div>
          <div>
            <div className="flex w-full flex-col divide-y-[.5px] divide-neutral-75">
              <div className="inline-flex h-8 w-full items-center justify-between p-1">
                <SupText variant="subtitle2">BASE RATE</SupText>
                <SupText variant="subtitle2">
                  <UiLabelApy vault={vault} period={period} unwrapped hideTooltip useFallback={useFallback} />
                </SupText>
              </div>
              {apyRewards.map((reward, index) => (
                <UiLabelRewardsTooltipLine reward={reward} key={`reward-tooltip-${vault.id}-apy-${index}`} />
              ))}
            </div>
            <div className="inline-flex h-8 w-full items-center justify-between border-[.5px] border-primary-100 bg-primary-25 p-1">
              <SupText variant="subtitle2" weight="bold" family="mono" className="text-xs leading-3 tracking-wide">
                NET APY
              </SupText>
              <SupText variant="subtitle2" weight="bold" family="mono" className="text-xs leading-3 tracking-wide">
                {netApy}
              </SupText>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
